import { useQuery, type QueryClient } from "@tanstack/react-query";
import type { EventFrame, HumanQuestion, PullRequest, Task } from "@agentsaas/protocol";
import { useServices } from "../app/services";
import { isEvent } from "./realtime";

/**
 * P2 协作数据（任务、PR、邮件、提问）：查询 + project:<pid> 通道的增量更新。
 * 列表类数据都整表缓存（项目内数量有限），实时事件直接 upsert 进缓存；gap 时全量刷新。
 */

export const ck = {
  tasks: (pid: string) => ["tasks", pid] as const,
  task: (tkid: string) => ["task", tkid] as const,
  pulls: (pid: string) => ["pulls", pid] as const,
  pull: (rid: string, num: number) => ["pull", rid, num] as const,
  pullDiff: (rid: string, num: number) => ["pull", rid, num, "diff"] as const,
  mailboxAll: ["mailbox"] as const,
  mailbox: (aid: string, box: "inbox" | "sent") => ["mailbox", aid, box] as const,
  questions: (pid: string) => ["questions", pid] as const,
  /** 本会话里观察到的任务状态变更（契约暂无历史字段） */
  taskHistory: (tkid: string) => ["taskHistory", tkid] as const,
};

export interface ObservedStatus {
  status: Task["status"];
  at: string;
}

export const upsertBy =
  <T>(key: (x: T) => string, item: T, prepend = false) =>
  (xs: T[] | undefined): T[] | undefined => {
    if (!xs) return xs;
    const k = key(item);
    if (xs.some((x) => key(x) === k)) return xs.map((x) => (key(x) === k ? item : x));
    return prepend ? [item, ...xs] : [...xs, item];
  };

export const pullKey = (p: Pick<PullRequest, "repoId" | "number">) => `${p.repoId}#${p.number}`;

export function useTasks(pid: string | undefined) {
  const { api } = useServices();
  return useQuery({ queryKey: ck.tasks(pid ?? ""), enabled: !!pid, queryFn: async () => (await api.call("taskList", { params: { pid: pid! }, query: {} })).items });
}

export function useTask(tkid: string | undefined) {
  const { api } = useServices();
  return useQuery({ queryKey: ck.task(tkid ?? ""), enabled: !!tkid, queryFn: () => api.call("taskGet", { params: { tkid: tkid! } }) });
}

export function usePulls(pid: string | undefined) {
  const { api } = useServices();
  return useQuery({
    queryKey: ck.pulls(pid ?? ""),
    enabled: !!pid,
    queryFn: async () => (await api.call("pullList", { params: { pid: pid! }, query: { state: "all", limit: 100 } })).items,
  });
}

export function usePull(rid: string | undefined, num: number) {
  const { api } = useServices();
  return useQuery({ queryKey: ck.pull(rid ?? "", num), enabled: !!rid && num > 0, queryFn: () => api.call("pullGet", { params: { rid: rid!, num } }) });
}

export function usePullDiff(rid: string | undefined, num: number) {
  const { api } = useServices();
  return useQuery({
    queryKey: ck.pullDiff(rid ?? "", num),
    enabled: !!rid && num > 0,
    queryFn: async () => (await api.call("pullDiff", { params: { rid: rid!, num }, query: {} })).files,
  });
}

export function useMailbox(aid: string | undefined, box: "inbox" | "sent") {
  const { api } = useServices();
  return useQuery({
    queryKey: ck.mailbox(aid ?? "", box),
    enabled: !!aid,
    queryFn: async () => (await api.call("mailbox", { params: { aid: aid! }, query: { box, limit: 100 } })).items,
  });
}

export function useOpenQuestions(pid: string | undefined) {
  const { api } = useServices();
  return useQuery({
    queryKey: ck.questions(pid ?? ""),
    enabled: !!pid,
    queryFn: async () => (await api.call("questionList", { params: { pid: pid! }, query: { status: "open" } })).items,
  });
}

/** 更新一条任务（列表 + 详情缓存）；状态变了就记进观察到的历史 */
export function putTask(qc: QueryClient, t: Task) {
  const prev = qc.getQueryData<Task>(ck.task(t.id)) ?? qc.getQueryData<Task[]>(ck.tasks(t.projectId))?.find((x) => x.id === t.id);
  if (prev && prev.status !== t.status)
    qc.setQueryData<ObservedStatus[]>(ck.taskHistory(t.id), (xs = []) => [...xs, { status: t.status, at: t.updatedAt }]);
  qc.setQueryData<Task[]>(ck.tasks(t.projectId), upsertBy((x) => x.id, t));
  qc.setQueryData(ck.task(t.id), t);
}

/** 更新一条 PR（列表 + 详情缓存） */
export function putPull(qc: QueryClient, pid: string, p: PullRequest) {
  qc.setQueryData<PullRequest[]>(ck.pulls(pid), upsertBy(pullKey, p, true));
  const prev = qc.getQueryData<PullRequest>(ck.pull(p.repoId, p.number));
  qc.setQueryData(ck.pull(p.repoId, p.number), p);
  // 有新提交（增删行数变化）时 diff 失效
  if (prev && (prev.additions !== p.additions || prev.deletions !== p.deletions)) void qc.invalidateQueries({ queryKey: ck.pullDiff(p.repoId, p.number) });
}

export function removeQuestion(qc: QueryClient, pid: string, id: string) {
  qc.setQueryData<HumanQuestion[]>(ck.questions(pid), (xs) => xs?.filter((q) => q.id !== id));
}

/** project:<pid> 通道上的 P2 事件；返回是否已处理 */
export function applyCollabEvent(qc: QueryClient, pid: string, e: EventFrame): boolean {
  if (isEvent(e, "task.created") || isEvent(e, "task.updated")) putTask(qc, e.data);
  else if (isEvent(e, "pr.updated")) putPull(qc, pid, e.data);
  else if (isEvent(e, "mail.received")) void qc.invalidateQueries({ queryKey: ck.mailboxAll });
  // question.asked 只带摘要（没有 options），拉一次完整列表
  else if (isEvent(e, "question.asked")) void qc.invalidateQueries({ queryKey: ck.questions(pid) });
  else if (isEvent(e, "question.answered")) removeQuestion(qc, pid, e.data.id);
  else return false;
  return true;
}

export function invalidateCollab(qc: QueryClient, pid: string) {
  for (const key of [ck.tasks(pid), ck.pulls(pid), ck.questions(pid), ck.mailboxAll, ["task"], ["pull"]]) void qc.invalidateQueries({ queryKey: key });
}
